import { ReportLevel } from '../report.model';

/**
 * Palette de données du rapport de diagnostic.
 *
 * Les valeurs viennent telles quelles de `../mocks/report/js/script.js`, et la
 * feuille de styles du document (§1 — Design tokens) porte l'avertissement qui
 * va avec : le trio catégoriel et le couple vert/rouge de statut ont été passés
 * au validateur data-viz (bande de clarté, plancher de chroma, séparation
 * daltonisme, contraste sur surface) dans les deux modes. On ne « range » pas
 * ces hexadécimaux ; on rejoue le validateur si on veut les changer.
 *
 * Pourquoi des couleurs en dur plutôt que des jetons CSS : ce sont des
 * *attributs SVG* (`fill`, `stroke`), pas des propriétés héritées. Un graphique
 * ne suit donc pas le thème tout seul — c'est pour cela que chaque composant
 * reçoit `theme` et recalcule ses couleurs.
 */

/** Les deux modes du rapport. */
export type ChartTheme = 'light' | 'dark';

/** Rôles de couleur consommés par les graphiques. */
export interface ChartPalette {
  /** Trio catégoriel : élève, secondaire, objectif. */
  readonly categorical: readonly [string, string, string];
  readonly good: string;
  readonly bad: string;
  readonly neutral: string;
  readonly track: string;
  readonly grid: string;
  readonly axis: string;
  readonly surface: string;
  readonly textPrimary: string;
  readonly textSecondary: string;
  readonly textMuted: string;
}

export const PALETTES: Readonly<Record<ChartTheme, ChartPalette>> = {
  light: {
    categorical: ['#2563EB', '#EA7317', '#7C3AED'],
    good: '#22C55E',
    bad: '#DC2626',
    neutral: '#9CA3AF',
    track: '#EEF2F7',
    grid: '#EDEFF3',
    axis: '#D6DAE1',
    surface: '#FFFFFF',
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textMuted: '#6B7280',
  },
  dark: {
    categorical: ['#3B82F6', '#CE7420', '#8B5CF6'],
    good: '#1EAF52',
    bad: '#C42E2E',
    neutral: '#6B7280',
    track: '#1F2733',
    grid: '#232C3A',
    axis: '#333D4D',
    surface: '#151A23',
    textPrimary: '#F1F5F9',
    textSecondary: '#A9B4C4',
    textMuted: '#8A94A6',
  },
};

/** Palette du thème courant. */
export function paletteFor(theme: ChartTheme): ChartPalette {
  return PALETTES[theme];
}

/**
 * Couleur d'un palier.
 *
 * Le rouge et le vert sont réservés aux extrêmes ; les deux paliers médians
 * empruntent au trio catégoriel plutôt qu'à un orange/jaune de statut, qui se
 * lirait comme une alerte. `null` (score hors de toute tranche) tombe sur le
 * neutre : pas de palier, pas de couleur de palier.
 */
export function levelColor(level: ReportLevel | null, palette: ChartPalette): string {
  if (level === null) return palette.neutral;
  if (level === 'weak') return palette.bad;
  if (level === 'average') return palette.categorical[1];
  if (level === 'good') return palette.categorical[0];
  return palette.good;
}

/* ─────────────────────────────────────────────────────────────────────────
   Couleurs d'identité des compétences
   ───────────────────────────────────────────────────────────────────────── */

/** Bande de teintes conservée avant le saut : 0–50°, du rouge à l'ambre. */
const LOW_BAND = 50;
/** 50–175° (jaune-vert puis vert) est sauté : c'est la couleur du « acquis ». */
const GAP = 125;
/** Étendue réellement parcourue : 50 + (360 − 175). */
const SPAN = 235;
/**
 * Pas de teinte — `SPAN * (1 − 1/φ)`, soit l'angle d'or ramené à l'étendue.
 *
 * Valeur littérale volontaire : le document de référence porte `89.76`, et le
 * markup pré-rendu a été relu avec. Recalculer `235 * (1 - 1/φ)` donne
 * 89.76201…, ce qui décale chaque teinte de quelques centièmes de degré et
 * change les chaînes `hsl()` produites (par ex. `254.0` devient `254.1`).
 */
const HUE_STRIDE = 89.76;
/** Clartés cyclées pour séparer deux teintes voisines. */
const TIERS_LIGHT = [33, 44, 55, 66];
const TIERS_DARK = [46, 56, 66, 74];
/** Saturations cyclées, décorrélées des clartés (4 et 3 sont premiers entre eux). */
const SATURATION_TIERS = [86, 62, 44];
/** Plancher de clarté dans l'ambre : sous ce seuil, le jaune vire au brun. */
const WARM_FLOOR_LIGHT = 50;
const WARM_FLOOR_DARK = 58;

/**
 * `count` couleurs d'identité distinctes, marche de teinte en angle d'or.
 *
 * Le vert est sauté d'un bloc : dans ce rapport le vert veut dire « acquis », et
 * une compétence verte dans l'anneau des lacunes serait un contresens. La
 * correction en cosinus assombrit autour du jaune (~70°) et éclaircit à
 * l'opposé, faute de quoi les teintes chaudes ressortent deux fois plus que les
 * froides à clarté nominale égale.
 */
export function competencyColors(count: number, theme: ChartTheme): string[] {
  const tiers = theme === 'dark' ? TIERS_DARK : TIERS_LIGHT;
  const warmFloor = theme === 'dark' ? WARM_FLOOR_DARK : WARM_FLOOR_LIGHT;
  const out: string[] = [];

  for (let i = 0; i < count; i += 1) {
    const t = (i * HUE_STRIDE) % SPAN;
    const hue = t < LOW_BAND ? t : t + GAP;
    const correction = Math.cos(((hue - 70) * Math.PI) / 180);
    let lightness = Math.max(26, Math.min(74, tiers[i % tiers.length] - 8 * correction));
    if (hue >= 35 && hue <= 50) lightness = Math.max(lightness, warmFloor);
    const saturation = SATURATION_TIERS[i % SATURATION_TIERS.length];
    out.push('hsl(' + hue.toFixed(1) + ' ' + saturation + '% ' + lightness.toFixed(1) + '%)');
  }
  return out;
}

/** Dernier résultat de `competencyColorMap`, indexé par (codes, thème). */
const COLOR_MAP_CACHE = new Map<string, Readonly<Record<string, string>>>();

/**
 * Couleur d'identité de chaque code, indexée par le code.
 *
 * **L'attribution est positionnelle** : `codes[i]` reçoit `colors[i]`. Changer
 * l'ordre de la liste des codes, en retirer un ou en ajouter un recolore donc
 * *tout* le rapport — anneau, plan hebdomadaire, pastilles de légende et puces
 * du plan. La liste canonique est `ReportView.skillCodes`, et c'est la raison
 * pour laquelle elle existe : elle fige les couleurs autant que l'ordre.
 *
 * Mémoïsé sur (codes joints, thème) : la carte est relue à chaque rendu de
 * section et recalculer 26 conversions HSL à chaque passe de détection de
 * changements ne sert à rien.
 */
export function competencyColorMap(
  codes: readonly string[],
  theme: ChartTheme,
): Readonly<Record<string, string>> {
  const key = codes.join('|') + ' ' + theme;
  const cached = COLOR_MAP_CACHE.get(key);
  if (cached) return cached;

  const colors = competencyColors(codes.length, theme);
  const map: Record<string, string> = {};
  for (let i = 0; i < codes.length; i += 1) map[codes[i]] = colors[i];

  COLOR_MAP_CACHE.set(key, map);
  return map;
}
